import { EHeadingStyleVariant, Heading } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import { aiSearchFilterAttributes } from '@/services/aiSearch/analytics';
import { type IAiSearchResponse, postAiSearch } from '@/services/aiSearch/client';
import { useApplyAiSearch } from '@/services/aiSearch/useApplyAiSearch';
import { trackEvent } from '@/services/track-event';

import { useAiSearch } from './AiSearchContext';
import AiSearchHelpPanel from './AiSearchHelpPanel';
import { pickExamplePrompts } from './examplePrompts';

const SURFACE = 'homepage';
const EXAMPLE_COUNT = 4;

type TMode = 'describe' | 'classic';

type TAiSearchHomeHeroProps = {
  /** The existing homepage search module, shown in "Classic search" mode. */
  classicSearch: ReactNode;
};

/**
 * Homepage hero for the AI-search experiment: a "Describe your trip" /
 * "Classic search" segmented control over either the free-text AI input or the
 * existing search module, plus the quiz entry point. A submitted description is
 * extracted (POST /api/ai-search) and applied as /rv-search filters, which
 * redirects to the results page with AI search left on for follow-ups.
 */
export default function AiSearchHomeHero({ classicSearch }: TAiSearchHomeHeroProps) {
  const { setEnabled, recordSearch, clearLastSearch } = useAiSearch();
  const applyAiSearch = useApplyAiSearch();

  // Landing on the homepage starts over: an older AI search shouldn't label
  // results reached through Classic search.
  useEffect(() => {
    clearLastSearch();
  }, [clearLastSearch]);

  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<TMode>('describe');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Set when the query was a question/handoff rather than a search — shows the
  // help panel instead of applying filters (which would navigate away).
  const [helpResp, setHelpResp] = useState<IAiSearchResponse | null>(null);
  // Set when the extractor understood nothing (outcome 'search' but no
  // filters) — without this the hero would wait forever on a navigation that
  // never happens.
  const [noCriteriaMsg, setNoCriteriaMsg] = useState<string | null>(null);
  // Picked once per mount, so every page load shows a different set. The hero
  // only renders after hydration, so the random pick can't mismatch SSR.
  const [examples] = useState(() => pickExamplePrompts(EXAMPLE_COUNT));

  const runSearch = useCallback(
    async (text?: string) => {
      const q = (text ?? query).trim();
      if (!q || loading) return;
      setLoading(true);
      setError(false);
      setNoCriteriaMsg(null);
      try {
        const resp = await postAiSearch({
          query: q,
          currentFilters: null,
          now: new Date().toISOString(),
          source: SURFACE,
        });
        trackEvent({
          event: 'AI Search Submitted',
          query: q,
          surface: SURFACE,
          outcome: resp.outcome ?? 'search',
          ...aiSearchFilterAttributes(resp),
        });
        // A question/handoff isn't a search — show the help panel in place
        // rather than enabling AI search and navigating to results.
        if (resp.outcome && resp.outcome !== 'search') {
          setHelpResp(resp);
          setLoading(false);
          return;
        }
        setHelpResp(null);
        // Nothing extracted (e.g. the query was gibberish): surface the
        // model's clarification and stop the spinner — there is no navigation
        // coming to unmount us.
        if (!applyAiSearch(resp)) {
          setNoCriteriaMsg(
            resp.summary ||
              'We couldn’t find any search criteria in that. Try adding a location or dates.',
          );
          setLoading(false);
          return;
        }
        // Keep the AI input in the /rv-search header so the renter can refine,
        // and let the results page describe this search in its heading.
        setEnabled(true);
        recordSearch(q, resp);
      } catch {
        setError(true);
        setLoading(false);
      }
    },
    [query, loading, setEnabled, recordSearch, applyAiSearch],
  );

  return (
    <div className="flex flex-col items-center pt-6 pb-8 md:pt-12 lg:pb-12">
      {/* Same serif display style as the standard homepage headline. */}
      <Heading
        component="h1"
        variant={EHeadingStyleVariant.H1Alt}
        className="not-italic font-normal text-center text-[40px] leading-[1.1] md:text-[80px] md:leading-none">
        Tell us about the trip.
        <br />
        We’ll find the rig.
      </Heading>

      <div
        role="tablist"
        aria-label="Search mode"
        className="flex p-1 mt-6 mb-6 rounded-full bg-gray-200/70 md:mt-8 gap-1">
        <ModeTab active={mode === 'describe'} onClick={() => setMode('describe')}>
          <span aria-hidden className="mr-1.5">
            ✦
          </span>
          Describe your trip
        </ModeTab>
        <ModeTab active={mode === 'classic'} onClick={() => setMode('classic')}>
          Classic search
        </ModeTab>
      </div>

      <div className="w-full max-w-4xl">
        {mode === 'describe' ? (
          <div>
            {/* Not a <form>: Enter is handled on keydown, matching the header input. */}
            <div className="flex items-center w-full py-2 pr-2 bg-white border border-transparent rounded-full shadow-lg pl-7 gap-3 focus-within:border-gray-900">
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runSearch();
                  }
                }}
                aria-label="Describe your trip"
                placeholder="Try “Family of 5 and a dog, Big Bend over spring break”"
                className="flex-1 min-w-0 py-2 text-lg bg-transparent border-none outline-none md:text-xl focus:outline-none"
              />
              <button
                type="button"
                aria-label="Search"
                onClick={() => runSearch()}
                className="flex items-center justify-center text-white bg-gray-900 rounded-full w-14 h-14 shrink-0 hover:bg-gray-800">
                {loading ? (
                  <span
                    className="w-6 h-6 border-2 border-white rounded-full border-t-transparent animate-spin"
                    aria-hidden
                  />
                ) : (
                  <SparkleSearchIcon />
                )}
              </button>
            </div>
            {error && (
              <p className="mt-2 ml-6 text-sm text-red-600">
                Couldn&apos;t run that search. Try again, or switch to Classic search.
              </p>
            )}
            {noCriteriaMsg && <p className="mt-2 ml-6 text-sm text-gray-700">{noCriteriaMsg}</p>}
            {helpResp && (
              <div className="mt-3">
                <AiSearchHelpPanel
                  resp={helpResp}
                  surface={SURFACE}
                  onDismiss={() => setHelpResp(null)}
                />
              </div>
            )}
            <div className="flex flex-wrap items-center justify-center mt-5 gap-3">
              <span className="text-base text-gray-500">Try:</span>
              {examples.map(example => (
                <button
                  key={example}
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setQuery(example);
                    runSearch(example);
                  }}
                  className={clsx(
                    'px-4 py-2 text-base text-left text-gray-900 border rounded-lg transition-colors disabled:cursor-wait',
                    query.trim() === example
                      ? 'bg-[#f1ebdc] border-gray-700'
                      : 'bg-white border-neutral-30 hover:bg-[#f1ebdc]',
                  )}>
                  {example}
                </button>
              ))}
            </div>
          </div>
        ) : (
          classicSearch
        )}
      </div>
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={clsx(
        'flex items-center px-4 py-2 text-base rounded-full transition-colors md:px-6 md:text-lg',
        active
          ? 'bg-white text-gray-900 shadow-sm font-medium'
          : 'text-gray-800 hover:text-gray-900',
      )}>
      {children}
    </button>
  );
}

function SparkleSearchIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden>
      <circle cx="11" cy="13" r="6" stroke="currentColor" strokeWidth="2" />
      <path d="M15.5 17.5 20 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M20 2.5c.3 1.9 1.1 2.7 3 3-1.9.3-2.7 1.1-3 3-.3-1.9-1.1-2.7-3-3 1.9-.3 2.7-1.1 3-3Z"
        fill="currentColor"
      />
    </svg>
  );
}
